import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionHeading from "./SectionHeading";
import ImageLightbox from "./ImageLightbox";
import { PORTFOLIO_CATEGORIES, PORTFOLIO_ITEMS } from "../data/siteContent";

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const [preview, setPreview] = useState(null);

  const filtered =
    filter === "All" ? PORTFOLIO_ITEMS : PORTFOLIO_ITEMS.filter((p) => p.category === filter);

  const openPreview = (item) => setPreview(item);

  const previewIndex = preview ? filtered.findIndex((p) => p.id === preview.id) : -1;

  const goPreview = useCallback(
    (dir) => {
      if (previewIndex < 0 || filtered.length === 0) return;
      const next = (previewIndex + dir + filtered.length) % filtered.length;
      setPreview(filtered[next]);
    },
    [previewIndex, filtered]
  );

  return (
    <section id="portfolio" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Portfolio"
          title="Our work speaks"
          titleAccent="before we do"
          subtitle="Click any project for a full-size preview. Filter by category to explore our work."
        />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 flex flex-wrap justify-center gap-2"
        >
          {PORTFOLIO_CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => {
                setFilter(cat);
                setPreview(null);
              }}
              className={`rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-wider transition ${
                filter === cat
                  ? "bg-gradient-to-r from-accent to-coral text-white shadow-glow"
                  : "border border-white/15 text-white/70 hover:border-accent/50 hover:text-white"
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        <motion.div layout className="columns-1 gap-5 sm:columns-2 lg:columns-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((item) => (
              <motion.article
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4 }}
                className={`group relative mb-5 break-inside-avoid overflow-hidden rounded-2xl border border-white/10 ${
                  item.tall ? "sm:mb-5" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => openPreview(item)}
                  className="block w-full cursor-zoom-in text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  aria-label={`View full size: ${item.category} project`}
                >
                  <img
                    src={item.image}
                    alt=""
                    className={`w-full object-cover transition duration-700 group-hover:scale-105 ${
                      item.tall ? "min-h-[420px]" : "aspect-[4/3]"
                    }`}
                  />
                  <motion.div className="absolute inset-0 flex items-center justify-center bg-ink/0 opacity-0 transition group-hover:bg-ink/50 group-hover:opacity-100 group-focus-visible:bg-ink/50 group-focus-visible:opacity-100">
                    <span className="flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-2.5 font-mono text-[10px] uppercase tracking-corporate text-white backdrop-blur">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                        />
                      </svg>
                      Enlarge
                    </span>
                  </motion.div>
                </button>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <ImageLightbox
        item={preview}
        onClose={() => setPreview(null)}
        onPrev={() => goPreview(-1)}
        onNext={() => goPreview(1)}
        showNav={filtered.length > 1}
      />
    </section>
  );
}
